/**
 * Enhanced Variant Picker - Compatible with Global PubSub
 * This version works harmoniously with the cart drawer pubsub
 */

class EnhancedVariantPicker extends HTMLElement {
  constructor() {
    super();
    this.debugMode = false;
    // console.log('EnhancedVariantPicker constructor called');
    
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  debug(...args) {
    if (this.debugMode) {
      console.log('[EnhancedVariantPicker]', ...args);
    }
  }

  init() {
    this.debug('Initializing');
    
    // Check how many variant pickers exist
    const allPickers = document.querySelectorAll('enhanced-variant-picker');
    this.debug(`Total variant pickers on page: ${allPickers.length}`);
    
    this.productForm = this.closest('product-form') || this.closest('form');
    this.debug('Product form found:', this.productForm);
    
    this.formId = this.dataset.formId || this.dataset.section;
    this.debug('Form ID:', this.formId);
    
    // Try multiple selectors to find the variant ID input
    this.variantIdInput = document.querySelector(`#product-form-${this.formId} input[name="id"]`) ||
                         this.productForm?.querySelector('input[name="id"]') ||
                         document.querySelector('product-form input[name="id"]') ||
                         document.querySelector('form[action*="/cart/add"] input[name="id"]');
    this.debug('Variant ID input found:', this.variantIdInput);
    
    if (!this.variantIdInput) {
      this.debug('WARNING: Could not find variant ID input with any selector');
    }
    
    // Find media gallery components
    this.mediaGallery = document.querySelector(`#MediaGallery-${this.dataset.section}`);
    this.thumbnails = document.querySelectorAll('[data-thumbnail]');
    this.mediaItems = document.querySelectorAll('[data-media-id]');
    
    this.debug('Media elements found:', {
      gallery: this.mediaGallery,
      thumbnails: this.thumbnails.length,
      items: this.mediaItems.length
    });
    
    this.radios = this.querySelectorAll('input[type="radio"]');
    this.debug('Radio buttons found:', this.radios.length);
    
    this.optionLabels = {};

    // Initialize option labels
    this.querySelectorAll('.option-container').forEach((container, index) => {
      const optionIndex = index + 1;
      const labelElement = container.querySelector('.form__label');
      if (labelElement) {
        this.optionLabels[`option${optionIndex}`] = labelElement;
      }
    });
    this.debug('Option labels initialized:', this.optionLabels);

    // Initial variant selection
    this.updateVariantState();
    
    // Add event listeners
    this.radios.forEach(radio => {
      radio.addEventListener('change', this.onVariantChange.bind(this));
    });

    // Remove variant change listeners to avoid circular updates
    // The variant picker should only dispatch events, not listen to them

    // Debug: Log initial state
    this.debug('Initial variant data:', this.getVariantData());

    // Reconcile the rendered form with the visible selection. The swatches
    // are native radios, so a click that lands before this script runs still
    // checks the radio, but its change event had no listener: the form's
    // variant id, price, gallery and URL all stayed on the server-rendered
    // variant (#1218750863108505). Also covers the browser restoring radio
    // state on reload/back, and the hidden-colors resync below.
    this.pendingChangeTarget = this.getReconcileTarget();

    // Listeners in bundle-pricing.js, atc-price-display.js and
    // sticky-add-to-cart.js attach on DOMContentLoaded, so the full variant
    // update runs once, after they exist (see onDeferredScriptsReady).
    this.deferredScriptsReady = false;
    onDeferredScriptsReady(() => {
      this.deferredScriptsReady = true;
      const target = this.pendingChangeTarget;
      this.pendingChangeTarget = null;
      if (target) {
        this.onVariantChange({ target });
      }
    });

    // Keep the form's variant id right immediately, so an Add to Cart before
    // the deferred update runs can't add the server-rendered variant.
    if (this.pendingChangeTarget) {
      this.updateFormVariantId();
    }
  }

  // Returns the input to replay through onVariantChange when the checked
  // radios don't match the variant id the form was rendered with, else null.
  getReconcileTarget() {
    const checkedColor = this.querySelector('input[name*="olor"]:checked');

    // Liquid moved the selection off a hidden color (custom.hidden_colors);
    // sync price, media, form id and URL to the visible selection.
    if (this.dataset.resyncOnInit === 'true' && checkedColor) {
      return checkedColor;
    }

    if (!this.variantIdInput) return null;
    const selectedVariant = this.getSelectedVariant();
    if (!selectedVariant || String(selectedVariant.id) === String(this.variantIdInput.value)) {
      return null;
    }

    // A color change also regroups/filters the gallery, so prefer the color
    // input when the color differs from the rendered variant's color.
    const renderedVariant = this.getVariantData()
      .find(variant => String(variant.id) === String(this.variantIdInput.value));
    const checkedInputs = Array.from(this.querySelectorAll('input[type="radio"]:checked'));
    const changedInputs = checkedInputs.filter(input => {
      const position = parseInt((input.dataset.index || '').replace('option', ''), 10) - 1;
      return !renderedVariant || renderedVariant.options[position] !== input.value;
    });

    if (checkedColor && changedInputs.includes(checkedColor)) return checkedColor;
    return changedInputs[0] || checkedInputs[0] || null;
  }

  onVariantChange(event) {
    if (!event.target) return;

    // Before the deferred listener scripts have attached, the events this
    // method dispatches would be lost; queue it and run it once they exist.
    // A queued color change is kept over a later size change so the replay
    // still filters the gallery; the replay reads all checked radios anyway.
    if (!this.deferredScriptsReady) {
      const isColorInput = input => /colou?r/i.test(input?.name || '');
      if (!isColorInput(this.pendingChangeTarget) || isColorInput(event.target)) {
        this.pendingChangeTarget = event.target;
      }
      this.updateVariantState();
      this.updateFormVariantId();
      return;
    }

    const selectedVariant = this.getSelectedVariant();
    if (!selectedVariant) return;

    this.debug('Variant changed to:', selectedVariant);

    // Check if this is a color change
    const changedInput = event.target;
    const optionName = changedInput.name?.toLowerCase() || '';
    const isColorChange = optionName.includes('color') || optionName.includes('colour');

    // Update state and form
    this.updateVariantState();
    this.updateFormVariantId();

    // Get media ID from variant
    const mediaId = this.getVariantMediaId(selectedVariant);
    this.debug('Variant media ID:', mediaId);

    if (mediaId) {
      // Update media through Dawn's expected method
      this.updateProductMedia(mediaId, selectedVariant, isColorChange);
    }

    // Filter gallery images by color when color changes
    if (isColorChange) {
      this.filterGalleryByColor(changedInput.value);
    }

    // Dispatch variant change events
    this.dispatchVariantChangeEvents(selectedVariant);

    // Update URL
    this.updateURL(selectedVariant);
  }

  getVariantMediaId(variant) {
    // Try different properties where media ID might be stored
    const mediaId = variant.featured_media?.id || 
                   variant.featured_image?.id || 
                   variant.media?.[0]?.id ||
                   variant.image_id;
    
    this.debug('Found media ID:', mediaId, 'for variant:', variant);
    return mediaId;
  }

  updateProductMedia(mediaId, variant, isColorChange = false) {
    this.debug('Updating product media with ID:', mediaId);

    // Find the media gallery component
    const mediaGallery = document.querySelector('media-gallery');
    if (!mediaGallery) {
      this.debug('Media gallery component not found');
      return;
    }

    const galleryViewer = document.querySelector(`#GalleryViewer-${this.dataset.section}`);
    const gallerySlider = document.querySelector(`#Slider-Gallery-${this.dataset.section}`);

    // If we found a media element, update it
    if (gallerySlider) {
      const mediaItems = gallerySlider.querySelectorAll('.product__media-item');
      this.debug('Found media items:', mediaItems.length);

      // Prefer Dawn's setActiveMedia (handles is-active, thumbnail state, color-group
      // regrouping, and thumbnail strip scroll). Falls back to manual toggle if absent.
      const fullMediaId = `${this.dataset.section}-${mediaId}`;
      if (typeof mediaGallery.setActiveMedia === 'function') {
        try {
          mediaGallery.setActiveMedia(fullMediaId, isColorChange);
        } catch (e) {
          this.debug('setActiveMedia threw:', e);
        }
      } else {
        mediaItems.forEach(item => {
          const itemMediaId = item.dataset.mediaId?.split('-').pop() || item.id.split('-').pop();
          item.classList.remove('is-active');
          if (itemMediaId === mediaId.toString()) {
            item.classList.add('is-active');
            if (isColorChange && typeof item.scrollIntoView === 'function') {
              try {
                item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
              } catch (err) {
                this.debug('Error scrolling to item:', err);
              }
            }
          }
        });
      }

      // Dispatch events to notify Dawn's media gallery
      const templateId = this.dataset.section;
      const events = [
        new CustomEvent('product:media:change', {
          detail: {
            variant,
            mediaId: `${templateId}-${mediaId}`,
            sectionId: templateId
          },
          bubbles: true
        }),
        new CustomEvent('variant:change', {
          detail: {
            variant,
            sectionId: templateId
          },
          bubbles: true
        })
      ];

      events.forEach(event => {
        this.debug('Dispatching event:', event.type, event.detail);
        mediaGallery.dispatchEvent(event);
        document.dispatchEvent(event);
        
        // Also dispatch to the viewer component
        if (galleryViewer) {
          galleryViewer.dispatchEvent(event);
        }
      });
    } else {
      this.debug('Gallery slider not found');
    }
  }

  filterGalleryByColor(color) {
    const gallery = document.querySelector('media-gallery[data-defer-variant-media]');
    if (!gallery) return;

    gallery.dataset.activeColor = color;

    // Toggle visibility of color-grouped items
    gallery.querySelectorAll('[data-color-group]').forEach(item => {
      const isActive = item.dataset.colorGroup === color;
      item.classList.toggle('color-active', isActive);

      // Hydrate deferred <template> content on first activation
      if (isActive) {
        const tpl = item.querySelector('template[data-deferred-media]');
        if (tpl) {
          item.appendChild(tpl.content);
          tpl.remove();
        }
      }
    });

    // Reset slider positions so pagination recalculates for visible items
    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    if (viewer?.resetPages) viewer.resetPages();
    const thumbs = gallery.querySelector('[id^="GalleryThumbnails"]');
    if (thumbs?.resetPages) thumbs.resetPages();
  }

  dispatchVariantChangeEvents(variant) {
    const eventDetail = { 
      variant,
      sectionId: this.dataset.section
    };
    
    // Method 1: Use CustomEvent (always works)
    const event = new CustomEvent('variant:change', {
      detail: eventDetail,
      bubbles: true,
      cancelable: false
    });
    
    this.debug('Dispatching variant:change event:', eventDetail);
    
    // Only dispatch once on document - this is sufficient for all listeners
    document.dispatchEvent(event);
    
    // Also use PubSub for compatibility with other Shopify components
    if (window.publish && window.PUB_SUB_EVENTS) {
      try {
        window.publish(window.PUB_SUB_EVENTS.variantChange, eventDetail);
      } catch (e) {
        this.debug('Could not publish to PubSub:', e);
      }
    }
  }

  updateURL(variant) {
    if (this.dataset.updateUrl === 'true' && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({ path: url.toString() }, '', url.toString());
      this.debug('Updated URL:', url.toString());
    }
  }

  onVariantChangeEvent(event) {
    if (!event.detail?.variant) return;
    
    const variant = event.detail.variant;
    
    // Update radio button selection
    variant.options.forEach((optionValue, index) => {
      const optionPosition = index + 1;
      const radio = this.querySelector(
        `input[data-index="option${optionPosition}"][value="${this.escapeQuotes(optionValue)}"]`
      );
      if (radio && !radio.checked) {
        radio.checked = true;
      }
    });
    
    this.updateVariantState();
  }

  escapeQuotes(str) {
    return str.replace(/"/g, '\\"').replace(/'/g, '\\\'');
  }

  updateVariantState() {
    // For each option, update the selected value display
    Object.keys(this.optionLabels).forEach(optionKey => {
      const selectedRadio = this.querySelector(`input[data-index="${optionKey}"]:checked`);
      const label = this.optionLabels[optionKey];
      
      if (selectedRadio && label) {
        // Find or create selected value display span
        let valueDisplay = label.querySelector('.selected-value-display');
        if (!valueDisplay) {
          valueDisplay = document.createElement('span');
          valueDisplay.className = 'selected-value-display';
          label.appendChild(valueDisplay);
        }
        
        valueDisplay.textContent = ` - ${selectedRadio.value}`;
      }
    });
    
    // Update disabled state of options based on available variants
    this.updateOptionAvailability();
  }

  updateOptionAvailability() {
    const variantData = this.getVariantData();
    if (!variantData.length) return;
    
    // Get currently selected options
    const selectedOptions = this.getSelectedOptions();
    
    // For each option position, determine which values are available
    const optionPositions = Object.keys(this.optionLabels).map(key => 
      parseInt(key.replace('option', ''))
    );
    
    optionPositions.forEach(position => {
      const optionIndex = position - 1;
      const availableOptionValues = this.getAvailableOptionValues(
        optionIndex, 
        selectedOptions, 
        variantData
      );
      
      // Update availability of option values
      const optionRadios = this.querySelectorAll(`input[data-index="option${position}"]`);
      optionRadios.forEach(radio => {
        const optionValue = radio.value;
        const isAvailable = availableOptionValues.includes(optionValue);
        const container = radio.closest('.variant-button-item, .variant-thumbnail-item');
        
        if (container) {
          if (isAvailable) {
            container.classList.remove('disabled');
            radio.disabled = false;
          } else {
            // Allow selection of out-of-stock variants for back-in-stock notifications
            // Keep visual styling but don't disable the input
            container.classList.add('disabled');
            radio.disabled = false; // Changed from true to false
          }
        }
      });
    });
  }

  getAvailableOptionValues(optionIndex, selectedOptions, variants) {
    const availableValues = new Set();
    
    variants.forEach(variant => {
      // Check if this variant matches the currently selected options
      // excluding the option we're checking availability for
      let isMatch = true;
      
      Object.keys(selectedOptions).forEach(optionPosition => {
        const position = parseInt(optionPosition.replace('option', '')) - 1;
        
        // Skip the option we're checking
        if (position === optionIndex) return;
        
        // Check if this option matches the selected value
        if (variant.options[position] !== selectedOptions[optionPosition]) {
          isMatch = false;
        }
      });
      
      // If all other options match, this option value is available
      if (isMatch && variant.available) {
        availableValues.add(variant.options[optionIndex]);
      }
    });
    
    return Array.from(availableValues);
  }

  getSelectedOptions() {
    const selectedOptions = {};
    
    Object.keys(this.optionLabels).forEach(optionKey => {
      const selectedRadio = this.querySelector(`input[data-index="${optionKey}"]:checked`);
      if (selectedRadio) {
        selectedOptions[optionKey] = selectedRadio.value;
      }
    });
    
    return selectedOptions;
  }

  getSelectedVariant() {
    const variantData = this.getVariantData();
    if (!variantData.length) return null;
    
    const selectedOptions = this.getSelectedOptions();
    const optionKeys = Object.keys(selectedOptions);
    
    // Find the variant that matches all selected options
    return variantData.find(variant => {
      return optionKeys.every(key => {
        const position = parseInt(key.replace('option', '')) - 1;
        return variant.options[position] === selectedOptions[key];
      });
    });
  }

  updateFormVariantId() {
    const selectedVariant = this.getSelectedVariant();
    
    if (selectedVariant && this.variantIdInput) {
      const oldValue = this.variantIdInput.value;
      this.variantIdInput.value = selectedVariant.id;
      this.debug(`Updated form variant ID from ${oldValue} to ${selectedVariant.id}`);
    } else {
      this.debug('Could not update form variant ID:', {
        selectedVariant: selectedVariant,
        variantIdInput: this.variantIdInput
      });
    }
  }

  getVariantData() {
    let variantData = [];
    
    const productJson = document.getElementById(`ProductJson-${this.dataset.section}`);
    if (productJson) {
      try {
        variantData = JSON.parse(productJson.textContent);
        this.debug('Parsed variant data:', variantData);
      } catch (e) {
        console.error('Error parsing product JSON:', e);
      }
    }
    
    return variantData;
  }
}

// Runs callback once every deferred script's DOMContentLoaded handler has run
// (a zero-delay timeout after DOMContentLoaded, since handlers registered by
// later deferred scripts run after ours). Runs on the next task if
// DOMContentLoaded already fired.
function onDeferredScriptsReady(callback) {
  let done = false;
  const run = () => {
    if (done) return;
    done = true;
    setTimeout(callback, 0);
  };
  const navigation = performance.getEntriesByType?.('navigation')?.[0];
  if (document.readyState === 'complete' || navigation?.domContentLoadedEventEnd > 0) {
    run();
    return;
  }
  document.addEventListener('DOMContentLoaded', run, { once: true });
  // Fallback if readyState/navigation timing can't tell DOMContentLoaded
  // already fired: load always follows it.
  window.addEventListener('load', run, { once: true });
}

// Define the custom element
if (!customElements.get('enhanced-variant-picker')) {
  console.log('Registering enhanced-variant-picker custom element');
  customElements.define('enhanced-variant-picker', EnhancedVariantPicker);
}

console.log('Enhanced variant picker script loaded');